"use client";

import React from 'react';
import Link from 'next/link';
import { normalizeSlug } from '@/lib/seo/slug';

type Friend = { personId: number; displayName?: string | null; avatarUrl?: string | null; isUnsafeContent?: boolean };

// 2026-05-02: removed the `max=6` default avatar cap. Profile sidebar now
// renders ALL accepted friends (the API already returns them all). If a
// user has hundreds of friends and the sidebar gets too tall, swap the
// `friends` slice below for a paginated/scrollable variant — but for
// current scale (most users < 30 friends) just rendering everything is
// the cleanest UX and matches what the user asked for.
export default function FriendsSidebarSection({ personId, max }: { personId: string | number; max?: number }) {
  const [friends, setFriends] = React.useState<Friend[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const res = await fetch(`/api/profile/friends?personId=${encodeURIComponent(String(personId))}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (!cancelled) setFriends(Array.isArray(j) ? (j as Friend[]) : Array.isArray(j?.friends) ? (j.friends as Friend[]) : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load friends');
      }
    })();
    return () => { cancelled = true; };
  }, [personId]);

  if (!friends || friends.length === 0) return null;
  // If a caller explicitly passes `max`, honor it. Default: show all.
  const list = typeof max === 'number' && max > 0 ? friends.slice(0, max) : friends;
  return (
    <section className="rounded-2xl border border-border bg-card text-card-foreground overflow-hidden shadow-sm">
      <header className="px-5 py-3 border-b border-border text-sm font-semibold">Friends</header>
      {/* flex-wrap, not grid-cols-3: a fixed grid leaves a mostly-empty card
          when someone has one or two friends. */}
      <div className="p-5 flex flex-wrap gap-4">
        {list.map((f, i) => (
          <Link key={`${f.personId}-${i}`} href={normalizeSlug(f.displayName) ? `/u/${normalizeSlug(f.displayName)}` : `/details/${encodeURIComponent(String(f.personId))}`} className="group block w-20 text-center transition-transform hover:scale-105">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={f.avatarUrl || '/img/default-avatar.png'}
              alt={f.displayName || 'Friend'}
              className="mx-auto h-16 w-16 rounded-full object-cover border-2 border-border shadow-sm"
              loading="lazy"
              decoding="async"
              onError={(e) => { e.currentTarget.src = '/img/default-avatar.png'; }}
            />
            <div className="mt-2 text-xs font-medium truncate group-hover:text-primary transition-colors">{f.displayName || 'Friend'}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}
